import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  cn,
  formatMonthLabel,
  MONTH_ABBR,
  monthKey,
  parseMonthLabel,
} from "../../../lib/utils";

interface MonthYearPickerProps {
  value: string;
  onChange: (period: string) => void;
}

const MonthYearPicker = ({ value, onChange }: MonthYearPickerProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseMonthLabel(value), [value]);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const [viewYear, setViewYear] = useState(selected.year || currentYear);

  useEffect(() => {
    if (selected.year) setViewYear(selected.year);
  }, [selected.year]);

  const isFutureMonth = (monthIndex: number) =>
    viewYear > currentYear || (viewYear === currentYear && monthIndex > currentMonth);
  const canGoNextYear = viewYear < currentYear;

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-9 items-center justify-center gap-2 rounded-md border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-medium text-[#171717] whitespace-nowrap shadow-sm transition-opacity hover:opacity-80"
      >
        <CalendarDays size={16} strokeWidth={2} />
        {formatMonthLabel(value)}
        <ChevronDown size={16} strokeWidth={2} />
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-1 w-64 rounded-md border border-[#E5E5E5] bg-white p-3 shadow-md">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Previous year"
              onClick={() => setViewYear((y) => y - 1)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#525252] hover:bg-[#F5F5F4]"
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
            <span className="text-sm font-semibold text-[#0A0A0A]">{viewYear}</span>
            <button
              type="button"
              aria-label="Next year"
              disabled={!canGoNextYear}
              onClick={() => canGoNextYear && setViewYear((y) => y + 1)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-[#525252] hover:bg-[#F5F5F4] disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight size={16} strokeWidth={2} />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-1">
            {MONTH_ABBR.map((abbr, index) => {
              const isSelected =
                index === selected.monthIndex && viewYear === selected.year;
              const disabled = isFutureMonth(index);
              return (
                <button
                  key={abbr}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onChange(monthKey(viewYear, index));
                    setOpen(false);
                  }}
                  className={cn(
                    "rounded-md py-2 text-sm font-medium transition-colors",
                    isSelected
                      ? "bg-[#3551F3] text-white"
                      : disabled
                        ? "cursor-not-allowed text-[#D4D4D4]"
                        : "text-[#374151] hover:bg-[#F5F5F4]"
                  )}
                >
                  {abbr}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
